import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ContentLayout,
  Header,
  SpaceBetween,
  Box,
  Button,
} from "@cloudscape-design/components";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useSupervisors } from "../hooks/useSupervisors";
import { useAgents } from "../hooks/useAgents";
import { usePagination } from "../hooks/usePagination";
import { useSummaryData } from "../hooks/useSummaryData";
import { useSummaryGeneration } from "../hooks/useSummaryGeneration";
import { useSelectionPersistence } from "../hooks/useSelectionPersistence";
import { useSplitPanel } from "../hooks/SplitPanelContext";
import SummaryConfigurationForm from "../components/insights/SummaryConfigurationForm";
import SummaryTable from "../components/insights/SummaryTable";
import ConfirmationModal from "../components/insights/ConfirmationModal";
import ResultModal from "../components/insights/ResultModal";
import InsightDetailPanel from "../components/insights/InsightDetailPanel";
import { DEFAULT_PREFERENCES, DEFAULT_SORTING_COLUMN } from "../constants/insightsConstants";
import * as api from "../api/api";

function InsightsCalls() {
  const { t } = useTranslation();
  const history = useHistory();
  const { openSplitPanel, closeSplitPanel, splitPanelOpen, handleSplitPanelToggle } = useSplitPanel();

  // Estados locales mínimos
  const [summaryName, setSummaryName] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const selection = useSelectionPersistence();
  const [filteringText, setFilteringText] = useState("");
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [sortingColumn, setSortingColumn] = useState(DEFAULT_SORTING_COLUMN);
  const [sortingDescending, setSortingDescending] = useState(true);

  // Estados para el panel de insight
  const [selectedCall, setSelectedCall] = useState(null);
  const [activeTabId, setActiveTabId] = useState("info");
  const [callDetails, setCallDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [insightError, setInsightError] = useState(null);

  // Callback para limpiar selecciones cuando se cargan supervisores
  const handleSupervisorsLoaded = React.useCallback((supervisor, agents) => {
    setSelectedSupervisor(supervisor);
    setSelectedAgents(agents);
  }, []);

  // Hooks personalizados
  const supervisors = useSupervisors(dateRange, handleSupervisorsLoaded);
  const agents = useAgents(dateRange, selectedSupervisor, t);
  const pagination = usePagination(dateRange, selectedSupervisor, selectedAgents);
  const summaryData = useSummaryData(
    dateRange,
    selectedSupervisor,
    selectedAgents,
    pagination,
    preferences
  );
  const selectedJobNames = useMemo(() => selection.getSelectedJobNames(), [selection.totalSelectedCount]); // eslint-disable-line react-hooks/exhaustive-deps
  const summaryGeneration = useSummaryGeneration(
    summaryName,
    dateRange,
    selectedJobNames,
    selection.totalSelectedCount,
    summaryData.tableItems,
    selectedSupervisor,
    selectedAgents
  );

  const pageSelectedItems = useMemo(
    () => selection.getPageSelectedItems(summaryData.tableItems),
    [summaryData.tableItems, selection.totalSelectedCount] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleSelectionChange = useCallback((newPageSelected) => {
    selection.handleSelectionChange(newPageSelected, summaryData.tableItems);
  }, [selection, summaryData.tableItems]);

  // Handler para cambio de supervisor (limpiar agentes cuando cambia)
  const handleSupervisorChange = (newSupervisor) => {
    setSelectedSupervisor(newSupervisor);
    setSelectedAgents([]); // Limpiar agentes cuando cambia supervisor
  };

  // Limpiar selección cross-page cuando cambian los filtros principales
  useEffect(() => {
    selection.clearAll();
  }, [dateRange, selectedSupervisor, selectedAgents]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handler para cambio de preferencias (resetear página si cambia tamaño)
  const handlePreferencesChange = ({ detail }) => {
    setPreferences(detail);
    if (detail.pageSize !== preferences.pageSize) {
      pagination.setCurrentPageIndex(1);
    }
  };

  // Handler para cambio de ordenación
  const handleSortingChange = ({ detail }) => {
    setSortingColumn(detail);
    setSortingDescending(detail.sortingDescending);
  };

  // Handler para cancelar
  const handleCancel = () => {
    history.push('/history-insights-calls');
  };

  // Función para obtener datos del insight desde la lambda get.js
  const fetchInsightData = useCallback(async (call) => {
    if (!call) {
      setCallDetails(null);
      setInsightError(null);
      return;
    }

    // Usar solo la key que devuelve el backend (igual que Lista de llamadas)
    const s3Key = call.key;
    if (!s3Key) {
      setInsightError(t("insights.errorLoadingInsight", { defaultValue: "Error al cargar la información del insight: No se pudo determinar el key del archivo" }));
      setCallDetails(null);
      return;
    }

    setLoadingDetails(true);
    setInsightError(null);
    setCallDetails(null);

    try {
      const response = await api.get(s3Key);

      // La respuesta puede ser un string JSON o un objeto
      let data;
      if (typeof response === 'string') {
        data = JSON.parse(response);
      } else {
        data = response;
      }

      // Guardar los datos completos (no solo ConversationAnalytics)
      setCallDetails(data);
      setInsightError(null);
    } catch (error) {
      console.error("Error fetching insight data:", error);
      setInsightError(
        t("insights.errorLoadingInsight", {
          defaultValue: "Error al cargar la información del insight"
        }) + `: ${error.message || error.toString()}`
      );
      setCallDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  }, [t]);

  // Handler para ver insight
  const handleViewInsight = (item) => {
    setSelectedCall(item);
    setActiveTabId("info");
    setCallDetails(null);
    setInsightError(null);

    // Abrir el panel inmediatamente con contenido de carga
    const loadingPanelContent = (
      <InsightDetailPanel
        selectedCall={item}
        callDetails={null}
        loadingDetails={true}
        insightError={null}
        activeTabId="info"
        setActiveTabId={setActiveTabId}
        history={history}
        t={t}
      />
    );

    // No pasar título, el header se maneja dinámicamente desde InsightDetailPanel
    openSplitPanel(loadingPanelContent, '');

    // Cargar datos del insight
    fetchInsightData(item);
  };

  // Actualizar el panel cuando cambian los datos
  useEffect(() => {
    if (selectedCall) {
      const panelContent = (
        <InsightDetailPanel
          selectedCall={selectedCall}
          callDetails={callDetails}
          loadingDetails={loadingDetails}
          insightError={insightError}
          activeTabId={activeTabId}
          setActiveTabId={setActiveTabId}
          history={history}
          t={t}
        />
      );

      // No pasar título, el header se maneja dinámicamente desde InsightDetailPanel
      openSplitPanel(panelContent, '');
    } else {
      closeSplitPanel();
    }
  }, [selectedCall, callDetails, activeTabId, loadingDetails, insightError, openSplitPanel, closeSplitPanel, history, t]);

  // Limpiar selectedCall cuando el panel se cierra
  useEffect(() => {
    if (!splitPanelOpen && selectedCall) {
      // Solo limpiar si el panel se cerró explícitamente (no durante la carga inicial)
      // Usar un pequeño delay para evitar conflictos con la apertura del panel
      const timeoutId = setTimeout(() => {
        if (!splitPanelOpen) {
          setSelectedCall(null);
          setCallDetails(null);
          setActiveTabId("info");
          setInsightError(null);
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [splitPanelOpen, selectedCall]);

  // Calcular pagesCount basándose en si hay más páginas
  const pagesCount = pagination.hasMorePages
    ? pagination.currentPageIndex + 1
    : pagination.currentPageIndex;

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t("insights.newCXSummaryDescription")}
        >
          {t("insights.newCXSummaryTitle")}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <SummaryConfigurationForm
          summaryName={summaryName}
          dateRange={dateRange}
          selectedSupervisor={selectedSupervisor}
          selectedAgents={selectedAgents}
          supervisors={supervisors.supervisors}
          agents={agents.agents}
          supervisorsEnabled={supervisors.supervisorsEnabled}
          agentsEnabled={agents.agentsEnabled}
          onSummaryNameChange={setSummaryName}
          onDateRangeChange={setDateRange}
          onSupervisorChange={handleSupervisorChange}
          onAgentsChange={setSelectedAgents}
          getAgentsPlaceholder={agents.getAgentsPlaceholder}
          t={t}
        />
        
        {dateRange && dateRange.startDate && dateRange.endDate && (
          <SummaryTable
            items={summaryData.tableItems}
            loading={summaryData.loading}
            selectedItems={pageSelectedItems}
            onSelectionChange={handleSelectionChange}
            totalSelectedCount={selection.totalSelectedCount}
            filteringText={filteringText}
            onFilteringTextChange={setFilteringText}
            currentPageIndex={pagination.currentPageIndex}
            pagesCount={pagesCount}
            onPageChange={pagination.setCurrentPageIndex}
            preferences={preferences}
            onPreferencesChange={handlePreferencesChange}
            sortingColumn={sortingColumn}
            sortingDescending={sortingDescending}
            onSortingChange={handleSortingChange}
            dateRange={dateRange}
            onViewInsight={handleViewInsight}
            t={t}
          />
        )}

        <Box float="right" padding={{ bottom: "l" }}>
          <SpaceBetween direction="horizontal" size="m">
            <Button
              variant="link"
              onClick={handleCancel}
            >
              {t("insights.cancel")}
            </Button>
            <Button
              variant="normal"
              onClick={summaryGeneration.generateSummary}
              disabled={!summaryGeneration.isCreateSummaryButtonEnabled()}
              loading={summaryGeneration.generatingSummary}
            >
              {t("insights.createSummary")}
            </Button>
          </SpaceBetween>
        </Box>
        {/* Empty space added to display date picker correctly */}
        {!(dateRange && dateRange.startDate && dateRange.endDate) && (
          <div style={{ height: '400px' }}>
          </div>
        )}

      </SpaceBetween>

      <ResultModal
        visible={summaryGeneration.showPostSuccessModal}
        onDismiss={() => summaryGeneration.setShowPostSuccessModal(false)}
        result={summaryGeneration.postRequestResult}
        t={t}
      />

      <ConfirmationModal
        visible={summaryGeneration.showConfirmationModal}
        onDismiss={() => summaryGeneration.setShowConfirmationModal(false)}
        onConfirm={summaryGeneration.processAfterConfirmation}
        summaryName={summaryName}
        dateRange={dateRange}
        selectedSupervisor={selectedSupervisor}
        selectedAgents={selectedAgents}
        verificationResult={summaryGeneration.verificationResult}
        generatingSummary={summaryGeneration.generatingSummary}
        t={t}
      />
    </ContentLayout>
  );
}

export default InsightsCalls; 
